import { useUpdateProfileAction } from "@app/account/hooks/use-profile";
import { majorsList } from "@core/grades/flags";
import { SubmitButton } from "@design-system/components/Button";
import {
  DatePickerInput,
  NumberInput,
  PhoneNumberInput,
  ResourceImageInput,
  SelectInput,
  TextInput,
} from "@design-system/components/Form";
import { userAtom } from "@framework/config/atoms";
import { trans, plainTrans } from "@mongez/localization";
import { Form } from "@mongez/react-form";

type EditProfileFormProps = {
  onSave?: () => void;
};

export function EditProfileForm({ onSave }: EditProfileFormProps) {
  const user = userAtom.useValue();
  const { editProfileSubmit } = useUpdateProfileAction(onSave);

  return (
    <Form onSubmit={editProfileSubmit}>
      <section className="mt-6 rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-7">
        <div className="mb-8 border-b border-slate-100 pb-5 dark:border-slate-800">
          <p className="text-sm font-bold text-primary">
            {trans("studentProfile.eyebrow")}
          </p>
          <h2 className="mt-2 text-2xl font-black text-slate-950 dark:text-white">
            {trans("studentProfile.profileDetailsTitle")}
          </h2>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500 dark:text-slate-400">
            {trans("studentProfile.profileDetailsDescription")}
          </p>
        </div>

        <div className="space-y-8">
          {/* Profile Image Section */}
          <div className="flex flex-col items-center space-y-4 rounded-3xl border border-slate-200 bg-slate-50/80 p-6 transition-colors duration-300 dark:border-slate-700 dark:bg-slate-950/60">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {trans("profilePicture")}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {trans("profilePictureDescription")}
              </p>
            </div>
            <div className="w-full max-w-xs">
              <ResourceImageInput
                name="image"
                defaultValue={user.image}
                reference={{
                  type: "user",
                  typeId: user.id?.toString(),
                }}
                label={trans("changeImage")}
                size="compact"
              />
            </div>
          </div>

          {/* Personal Information Section */}
          <div className="space-y-6">
            <div className="border-b border-gray-200 pb-4 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {trans("personalInformation")}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {trans("personalInformationDescription")}
              </p>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              <TextInput
                label={trans("address")}
                disabled={!!user.address}
                name="address"
                defaultValue={user.address}
                placeholder={plainTrans("address")}
                required
              />
              <DatePickerInput
                id="birthDate"
                label={trans("birthDate")}
                name="birthDate"
                placeholder={plainTrans("birthDate")}
                disabled={!!user.birthDate}
                defaultValue={user.birthDate?.timestamp}
                required
              />
              <TextInput
                label={trans("account.customId")}
                name="customId"
                placeholder={plainTrans("account.customId")}
                disabled={!!user.customId}
                defaultValue={user.customId}
              />
            </div>
          </div>

          {/* Academic Information Section */}
          <div className="space-y-6">
            <div className="border-b border-gray-200 pb-4 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {trans("academicInformation")}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {trans("academicInformationDescription")}
              </p>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              <TextInput
                label={trans("schoolName")}
                name="schoolName"
                placeholder={plainTrans("schoolName")}
                disabled={!!user.schoolName}
                defaultValue={user.schoolName}
                required
              />
              <NumberInput
                label={trans("lastYearScore")}
                name="lastYearScore"
                placeholder={plainTrans("lastYearScore")}
                disabled={!!user.lastYearScore}
                defaultValue={user.lastYearScore}
              />
              <SelectInput
                label={trans("major")}
                name="major"
                placeholder={plainTrans("major")}
                disabled={!!user.major}
                defaultValue={user.major}
                required
                options={majorsList.map((major) => ({
                  label: trans(`auth.${major}`),
                  value: major,
                }))}
              />
            </div>
          </div>

          {/* Parent Information Section */}
          <div className="space-y-6">
            <div className="border-b border-gray-200 pb-4 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {trans("parentInformation")}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {trans("parentInformationDescription")}
              </p>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              <PhoneNumberInput
                label={trans("parentPhoneNumber")}
                name="parentPhoneNumber"
                placeholder={plainTrans("parentPhoneNumber")}
                disabled={!!user.parentPhoneNumber}
                defaultValue={user.parentPhoneNumber}
                required
              />
              <TextInput
                label={trans("parentJob")}
                name="parentJob"
                placeholder={plainTrans("parentJob")}
                disabled={!!user.parentJob}
                defaultValue={user.parentJob}
                required
              />
            </div>
          </div>
        </div>

        <div className="mt-8 flex justify-end">
          <SubmitButton className="h-auto rounded-2xl px-8 py-3 text-base font-bold text-primary-foreground shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl">
            {trans("save")}
          </SubmitButton>
        </div>
      </section>
    </Form>
  );
}
